import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Button, Link, Message, Messages, Modal, Spinner, useAlert } from "capitalrx-components-2.0/lib/general";
import { Semibold, Body, Heading, Subtitle } from "capitalrx-components-2.0/lib/typography";
import {
  Checkbox,
  Datebox,
  DateSerializer,
  Dropdown,
  Form,
  RadioGroup,
  SubmitButton,
  TextArea,
  Textbox,
  UseFormReturn,
} from "capitalrx-components-2.0/lib/forms";

import { validatePACaseAPI, validateRenewalCodeAPI, PACase, getQuestionnairesAPI, ProviderManagement, getProviderManagementAPI } from "@/api";
import { ValidateResultType } from "@/api/interfaces/PACase";
import { DispensingSiteDisplay, DAW_CODES } from "@/constants";
import { DetailsList, DiagnosisCodeMultiSelect, PACaseCreateForm } from "@/components";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, numberValidator, npiValidator, validatePhoneNumber, formatPhoneWithExtension, currentEnvironment } from "@/utils";

import styles from "./PACreateForm.module.scss";
import { NDCDropdown } from "../Medical/BenefitSearchForm/NDCDropdown";
import { useFormState } from "react-hook-form";


interface ValidateResult {
  result: string;
  reject_codes: string[];
  pa_needed: boolean;
  show_dispensing_site: boolean;
}

interface FormProps {
  goToNextStep: Function;
  form: UseFormReturn<PACaseCreateForm>;
  resetQuestionnaires: Function;
  isRenewal?: boolean;
  setIsRenewal?: (val: boolean) => void;
  initialRenewalNpi?: string;
  initialRenewalCode?: string;
  previousCaseId?: number | null;
  setPreviousCaseId?: (id: number | null) => void;
  backToEditTrigger?: number;
}

enum FormStates {
  TestClaim = 1,
  Questionnaire = 2,
  SendDraft = 3,
}

export const CaseTestClaimForm: FC<FormProps> = ({
  goToNextStep,
  form,
  resetQuestionnaires,
  isRenewal = false,
  initialRenewalNpi = "",
  initialRenewalCode = "",
  setIsRenewal,
  previousCaseId,
  setPreviousCaseId,
  backToEditTrigger = 0,
}) => {

  const navigate = useNavigate();
  const showAlert = useAlert();
  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const getProvider = useCallback(async () => {
    try {
      // Find provider management record and process response
      const authToken = getAuthToken();
      if (!authToken) {
        return;
      }
      const pmResponse = await getProviderManagementAPI(authToken);
      setProvider(pmResponse.provider);

    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    }
  }, []);

  const { getAuthToken } = useContext(AuthenticationContext);
  useEffect(() => {
    getProvider();
  }, []);

  const { primary_color } = useContext(ThemeSelectorContext);

  const [patientError, setPatientError] = useState<string>();
  const [drugError, setDrugError] = useState<string>();
  const [prescriberError, setPrescriberError] = useState<string>(null);
  const [renewalCodeError, setRenewalCodeError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [hasDispensingSite, setHasDispensingSite] = useState<boolean>(false);

  const [caseType, setCaseType] = useState<"new" | "renewal">(isRenewal ? "renewal" : "new");

  const watchCaseType = form.watch("case_type") as "new" | "renewal" | undefined;
  useEffect(() => {
    if (watchCaseType === "new" || watchCaseType === "renewal") {
      setCaseType(watchCaseType);
      if (setIsRenewal) setIsRenewal(watchCaseType === "renewal");
    }
  }, [watchCaseType, setIsRenewal]);

  const { isSubmitting } = useFormState({
    control: form.control,
  });
  // Keep form fields in sync when URL params arrive after mount
  useEffect(() => {
    if (initialRenewalNpi) {
      form.setValue("npi", initialRenewalNpi);
    }
    if (initialRenewalCode) {
      form.setValue("renewal_code", initialRenewalCode);
    }
    if (isRenewal) setCaseType("renewal");
  }, [initialRenewalNpi, initialRenewalCode, isRenewal]);

  useEffect(() => {
    if (backToEditTrigger > 0) {
      setEditView(true);
    }
  }, [backToEditTrigger]);

  const dispensingSite = form.watch("dispensing_site");
  const alternateFaxNumber = form.watch("alternate_fax_number");
  useEffect(() => {
    if (alternateFaxNumber) {
      const formatted = formatPhoneWithExtension(alternateFaxNumber);
      if (formatted !== alternateFaxNumber) {
        form.setValue("alternate_fax_number", formatted);
      }
    }
  }, [alternateFaxNumber, form]);

  const getValidateResult = () => {

    const formValues = form.getValues();
    let resultDisplay = "";
    if (formValues?.review_type === "non_formulary_exception") {
      resultDisplay = "Non-formulary drug";
    } else if (formValues?.review_type === "step_therapy_exception") {
      resultDisplay = "Step therapy required";
    } else if (formValues?.review_type === "prior_authorization_exception") {
      resultDisplay = "Prior authorization required";
    } else if (formValues?.review_type === "no_review_needed") {
      resultDisplay = "No prior authorization required";
    } else if (formValues?.review_type === "tiering_exception") {
      resultDisplay = "Prior authorization required";
    }

    const showDispensingSite = formValues?.test_claim?.dispensing_sites && formValues?.test_claim?.dispensing_sites.includes("prescriber_administered");
    if (!showDispensingSite && !hasDispensingSite) {
      form.setValue("dispensing_site", "pharmacy");
      form.setValue("facility_npi", null);
      dispensingSiteFormSubmitted();
    }

    return {
      result: resultDisplay,
      reject_codes: formValues?.test_claim?.reject_codes,
      pa_needed: formValues?.review_type !== "no_review_needed",
      show_dispensing_site: showDispensingSite,
    };
  };
  const [editView, setEditView] = useState<boolean>(!form.getValues("test_claim"));
  const [validateResult, setValidateResult] = useState<ValidateResult>({
    result: null,
    reject_codes: [],
    pa_needed: true,
    show_dispensing_site: true,
  });

  const { three_six_twelve_columns, isCompressed } = useResponsive({
    three_six_twelve_columns: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });
  const StackOrShelf = isCompressed ? Stack : Shelf;

  const displayError = useCallback((error: string) => {
    showAlert({ type: "error", style: "banner", message: error });
  }, [showAlert]);

  const resetFormState = () => {
    setPatientError("");
    setDrugError("");
    setPrescriberError("");
    setRenewalCodeError(null);
    setValidateResult({
      result: null,
      reject_codes: [],
      pa_needed: true,
      show_dispensing_site: true,
    });
  };

  const renewalFormSubmitted = useCallback(async () => {
    resetFormState();
    setRenewalCodeError(null);
    logEvent(ANALYTIC_EVENTS.PA_CASE_RENEW.validate);

    const formData = form.getValues();
    const lookupNpi = formData.npi;
    const lookupCode = formData.renewal_code;

    let renewal: PACase.PACaseValidateRenewalCodeResponse;
    try {
      renewal = await validateRenewalCodeAPI({ npi: lookupNpi, code: lookupCode });
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.renewCodeNotFound);
      setRenewalCodeError("No renewal case found");
      return;
    }

    if (setPreviousCaseId) setPreviousCaseId(renewal.case_id);
    form.setValue("previous_case_id", renewal.case_id);

    const requiredFields = {
      npi: renewal.npi,
      external_member_id: renewal.external_member_id,
      member_first_name: renewal.member_first_name,
      member_last_name: renewal.member_last_name,
      member_date_of_birth: renewal.member_date_of_birth,
      ndc: renewal.ndc,
      quantity_dispensed: renewal.quantity_dispensed,
      days_supply: renewal.days_supply,
      directions_for_use: renewal.directions_for_use,
      daw_code: renewal.daw_code != null ? String(renewal.daw_code) : null,
      icd_10: renewal.icd_10 ?? [],
    };
    Object.entries(requiredFields).forEach(([field, value]) => {
      if (value != null && value !== "") {
        form.setValue(field as keyof typeof requiredFields, value);
      }
    });

    const optionalFields = {
      is_urgent: renewal.is_urgent,
      alternate_fax_number: renewal.alternate_fax_number,
      provider_fax: renewal.provider_fax,
    };
    Object.entries(optionalFields).forEach(([field, value]) => {
      if (value != null && value !== "") {
        form.setValue(field as keyof typeof optionalFields, value);
      }
    });

    try {
      const testClaimData = form.getValues();
      const testClaimResult = await validatePACaseAPI({
        external_member_id: testClaimData.external_member_id,
        member_first_name: testClaimData.member_first_name,
        member_last_name: testClaimData.member_last_name,
        member_date_of_birth: testClaimData.member_date_of_birth,
        ndc: testClaimData.ndc,
        npi: testClaimData.npi,
        quantity_dispensed: testClaimData.quantity_dispensed,
        days_supply: testClaimData.days_supply,
        icd_10: testClaimData.icd_10,
        daw_code: String(testClaimData.daw_code),
      });
      await handleTestClaimResult(testClaimResult);

    } catch (error) {
      console.error(error);
      displayError("Failed to process test claim. Please try again later.");
      logEvent(ANALYTIC_EVENTS.ERRORS.caseValidate);
    }
  }, [form, setPreviousCaseId]);

  const hasAutoSubmittedRenewal = useRef<boolean>(false);
  useEffect(() => {
    if (!isRenewal || !initialRenewalNpi || !initialRenewalCode || hasAutoSubmittedRenewal.current) {
      return;
    }

    hasAutoSubmittedRenewal.current = true;
    renewalFormSubmitted();
  }, [initialRenewalCode, initialRenewalNpi, isRenewal, renewalFormSubmitted]);

  const prefillLoggedInAccount = useCallback(async () => {
    if (!provider || !provider.billing_npi) return;
    try {
      if (!isRenewal) {
        form.setValue("npi", provider.billing_npi);
      }
      form.setValue("provider_first_name", provider.authorized_official_first_name);
      form.setValue("provider_last_name", provider.authorized_official_last_name);
      form.setValue("provider_phone", provider.provider_phone);
      form.setValue("provider_fax", provider.provider_fax);
      form.setValue("provider_email", provider.provider_email);
      form.setValue("provider_address", provider.physical_address_line_1);
      form.setValue("provider_address_2", provider.physical_address_line_2);
      form.setValue("provider_city", provider.physical_address_city);
      form.setValue("provider_state", provider.physical_address_state);
      form.setValue("provider_zip", provider.physical_address_zip_code);
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    }
  }, [form, isRenewal, provider]);

  const [demoDrugSearchValue, setDemoDrugSearchValue] = useState("");
  const [demoDiagnosisSearchValue, setDemoDiagnosisSearchValue] = useState("");
  const nextStepRef = useRef<FormStates>();

  const fillDemoDataClick = useCallback(() => {
    if (currentEnvironment === "development") {
      form.setValue("npi", "9990000001");
      setDemoDrugSearchValue("99901000202");
      setDemoDiagnosisSearchValue("M7989");
      form.setValue("external_member_id", "99880002");
      form.setValue("member_first_name", "Marco");
      form.setValue("member_last_name", "Rambo");
      form.setValue("member_date_of_birth", "03/22/1975");
      form.setValue("ndc", "99901000202");
      form.setValue("icd_10", ["M7989"], { shouldValidate: true, shouldDirty: true });
    }

    if (currentEnvironment === "staging") {
      form.setValue("npi", "1477661379");
      setDemoDrugSearchValue("00169413001");
      setDemoDiagnosisSearchValue("E119");
      form.setValue("external_member_id", "12345");
      form.setValue("member_first_name", "Keith");
      form.setValue("member_last_name", "Tkachuk");
      form.setValue("member_date_of_birth", "01/01/1985");
      form.setValue("ndc", "00169413001");
      form.setValue("icd_10", ["E119"], { shouldValidate: true, shouldDirty: true });
    }

    form.setValue("quantity_dispensed", 1);
    form.setValue("days_supply", 30);
    form.setValue("directions_for_use", "Inject once weekly");
    form.setValue("daw_code", "0");
  }, [form]);


  const getQuestionnaires = useCallback(async () => {
    const data = form.getValues();

    // Don't fetch questionnaires if they don't need a PA
    if (!data.review_type || data.review_type === "no_review_needed") {
      return;
    }

    try {
      const questionnaires = await getQuestionnairesAPI(
        {
          drug_ndc: data.ndc,
          claim_compound_code: data.test_claim.compound_code,
          claim_reject_codes: data.test_claim.reject_codes,
          person_code: data.test_claim.person_code,
          external_account_id: data.test_claim.external_account_id,
          external_member_id: data.test_claim.external_member_id,
          member_client_id: data.test_claim.client_id,
          member_group_id: data.test_claim.group_id,
          programs: data.test_claim.programs,
          review_types: [data.review_type],
          dispensing_site: data.dispensing_site,
          formulary_identifier: data.test_claim.formulary_identifier,
        },
      );

      // form success
      form.setValue("questionnaires", questionnaires);
    } catch (error) {
      console.error(error);
      showAlert({ type: "error", style: "banner", message: "Failed to load clinical criteria questions" });
    }
  },
    [showAlert, form],
  );

  const formSubmitted = useCallback(
    async (data: Partial<PACaseCreateForm>) => {
      // Remove all errors
      resetFormState();

      // Analytics
      logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.validate);

      const sanitizedData = {
        external_member_id: data.external_member_id,
        member_first_name: data.member_first_name,
        member_last_name: data.member_last_name,
        member_date_of_birth: data.member_date_of_birth,
        ndc: data.ndc,
        npi: data.npi,
        quantity_dispensed: data.quantity_dispensed,
        days_supply: data.days_supply,
        icd_10: data.icd_10,
        daw_code: data.daw_code,
      };

      try {
        // Send test claim and process response
        const testClaimResult = await validatePACaseAPI(sanitizedData);
        await handleTestClaimResult(testClaimResult);
      } catch (error) {
        console.error(error);
        displayError("Failed to process test claim. Please try again later.");
        // analytics log validate fail
        logEvent(ANALYTIC_EVENTS.ERRORS.caseValidate);
      }
    },
    [],
  );

  const dispensingSiteFormSubmitted = useCallback(
    async () => {
      setHasDispensingSite(true);
      await getQuestionnaires();
      if (nextStepRef.current === FormStates.SendDraft) {
        goToNextStep(nextStepRef.current);
        nextStepRef.current = null;
      } else {
        goToNextStep();
      }
    },
    [],
  );

  const handleTestClaimResult = async (testClaimResult: PACase.PACaseValidateResponse) => {
    const result = testClaimResult.result;
    // Error Handling
    if (result === ValidateResultType.PatientNotFound) {
      setPatientError("No patient found - check that all information entered is correct");
      return;
    } else if (result === ValidateResultType.DrugNotFound) {
      setDrugError("No drug found - check that NDC is correct");
      return;
    } else if (result === ValidateResultType.PrescriberNotFound) {
      setPrescriberError("Prescriber not found.");
      return;
    } else if (
      [
        ValidateResultType.PatientSearchFail,
        ValidateResultType.PrescriberSearchFail,
        ValidateResultType.TestClaimFail,
      ].includes(result)
    ) {
      displayError("Failed to process test claim. Please try again later.");
      return;
    }

    // Set Review Type
    if (result === ValidateResultType.Nonformulary) {
      form.setValue("review_type", "non_formulary_exception");
    } else if (result === ValidateResultType.StepTherapyException) {
      form.setValue("review_type", "step_therapy_exception");
    } else if (result === ValidateResultType.PARequired) {
      form.setValue("review_type", "prior_authorization_exception");
    } else if (result === ValidateResultType.PANotRequired) {
      form.setValue("review_type", "no_review_needed");
    }

    // form success
    form.setValue("test_claim", testClaimResult.test_claim);

    const testClaim = testClaimResult.test_claim;

    if (testClaim?.prescriber_first) {
      form.setValue("provider_first_name", testClaim.prescriber_first);
    }
    if (testClaim?.prescriber_last) {
      form.setValue("provider_last_name", testClaim.prescriber_last);
    }
    if (testClaim?.provider_phone) {
      form.setValue("provider_phone", testClaim.provider_phone);
    }
    if (testClaim?.provider_fax) {
      form.setValue("provider_fax", testClaim.provider_fax);
    }
    if (testClaim?.provider_address) {
      form.setValue("provider_address", testClaim.provider_address);
    }
    if (testClaim?.provider_address_2) {
      form.setValue("provider_address_2", testClaim.provider_address_2);
    }
    if (testClaim?.provider_city) {
      form.setValue("provider_city", testClaim.provider_city);
    }
    if (testClaim?.provider_state) {
      form.setValue("provider_state", testClaim.provider_state);
    }
    if (testClaim?.provider_zip) {
      form.setValue("provider_zip", testClaim.provider_zip);
    }

    // Set the validate result so we can display it nicely
    setValidateResult(getValidateResult());
    setEditView(false);
  };

  const goToPAHome = useCallback(() => {
    logEvent(isRenewal ? ANALYTIC_EVENTS.PA_CASE_RENEW.back : ANALYTIC_EVENTS.PA_CASE_CREATE.back);
    navigate(getThemeURL("PA_SEARCH"));
  }, [navigate, isRenewal]);

  const editModal = (
    <Modal.Approve
      visible={showEditModal}
      onClose={(approved: boolean) => {
        if (approved) {
          // remove result of this form so it sets back to this step
          form.setValue("test_claim", null);
          resetQuestionnaires();
          setHasDispensingSite(false);
          setValidateResult(getValidateResult());
          setEditView(true);
          goToNextStep();
        }
        setShowEditModal(false);
      }}
      title="Edit Case Details"
      denyContent="Cancel"
      approveContent="Continue"
    >
      <Body>
        Editing case details will require a new test claim.
        Any information entered after the test claim result will be lost.
      </Body>
    </Modal.Approve>
  );

  const getDetails = () => {
    const data = form.getValues();

    let drugName = "";
    if (data.test_claim?.drug_name) {
      drugName = data.test_claim.drug_name;
      if (data.test_claim?.drug_dosage_form) {
        drugName += `, ${data.test_claim.drug_dosage_form}`;
      }
      if (data.test_claim?.drug_dosage) {
        drugName += ` (${data.test_claim.drug_dosage})`;
      }
      drugName += `, Quantity: ${data.quantity_dispensed}, Days Supply: ${data.days_supply}`;
    }

    let prescriberName = "";
    if (data.test_claim?.prescriber_first && data.test_claim?.prescriber_last) {
      prescriberName = `${data.test_claim?.prescriber_first} ${data.test_claim?.prescriber_last}`;
    }

    let displayValues = {
      "Prescriber NPI": data.npi,
      "Alternate Fax Number": data?.alternate_fax_number ?? provider?.authorized_official_fax ?? "N/A",
      "Urgent": data.is_urgent ? "Yes" : "No",
      "Member ID": data.external_member_id,
    };

    if (prescriberName) {
      displayValues = { ...{ "Prescriber": prescriberName }, ...displayValues };
    }

    if (drugName) {
      displayValues["Drug"] = drugName;
    }
    displayValues["NDC"] = data.ndc;
    displayValues["DAW Code"] = DAW_CODES[data.daw_code];

    if (hasDispensingSite) {
      displayValues["Dispensing Site"] = DispensingSiteDisplay[data.dispensing_site];
      if (data.dispensing_site === "prescriber_administered") {
        displayValues["Dispensing Provider NPI"] = data.facility_npi;
      }
    }

    return displayValues;
  };

  useEffect(() => {
    prefillLoggedInAccount();
  }, [form, provider]);

  return (
    <Stack>
      {editModal}
      {editView
        && <Form form={form} onSubmit={caseType === "renewal" ? renewalFormSubmitted : formSubmitted}>
            <Stack>
              <Stack>
                <Shelf className={styles.PACreateFormInstructionsPanel}>
                  <Body size="small">Review prior authorization criteria before submitting</Body>
                  <Shelf.Spacer />
                  <Semibold>
                    <Link to={getThemeURL("CRITERIA")}>
                      View Standard Criteria
                    </Link>
                  </Semibold>
                </Shelf>
              </Stack>
              <div className={styles.PACreateFormInstructionsPanel}>
                <Stack spacing="none">
                  <Semibold>Instructions</Semibold>
                  <ul>
                    <li>Enter case, patient, and drug information to run a test claim and verify that a prior authorization is required</li>
                    <li><Semibold>To speed up the decision process and prevent unnecessary denials,</Semibold> complete the resulting clinical criteria questions in full</li>
                    <li>After submitting the prior authorization, upload any documentation that is important for the review (e.g. chart notes, lab data)</li>
                  </ul>
                </Stack>
              </div>

              <Stack>
                <Heading style={{ "paddingTop": "1rem" }}>Case</Heading>
                <Grid>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <RadioGroup.Field
                      testId="test__case_type"
                      name="case_type"
                      hideLabel
                      orientation="horizontal"
                      defaultValue="new"
                    >
                      <RadioGroup.Option value="new">Start a new case</RadioGroup.Option>
                      <RadioGroup.Option value="renewal">Renew a case</RadioGroup.Option>
                    </RadioGroup.Field>
                  </Grid.Span>
                </Grid>

                {!!renewalCodeError && (
                  <Message appearance={Messages.Error} visible testId="test__renewal_code_error">
                    {renewalCodeError}
                  </Message>
                )}

                {caseType === "renewal" && (
                  <Grid>
                    <Grid.Span columns={three_six_twelve_columns}>
                      <Textbox.Field
                        testId="test__renewal_npi"
                        label="Prescriber NPI"
                        name="npi"
                        validator={npiValidator}
                        required
                      />
                    </Grid.Span>
                    <Grid.Break />
                    <Grid.Span columns={three_six_twelve_columns}>
                      <Textbox.Field
                        testId="test__renewal_code"
                        label="Case Renewal Code"
                        name="renewal_code"
                        required
                        instructions="Enter renewal code from fax or email. If you do not have a code, start a new PA case."
                      />
                    </Grid.Span>
                  </Grid>
                )}

                {caseType === "new" && (
                  <>
                    {prescriberError && <Message appearance={Messages.Error} visible>{prescriberError}</Message>}
                    <Grid>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field testId="test__npi" label="Prescriber NPI" name="npi" validator={npiValidator} required />
                      </Grid.Span>
                      <Grid.Break />
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field
                          testId="test__alternate_fax_number"
                          label="Alternate Fax Number"
                          name="alternate_fax_number"
                          validator={validatePhoneNumber}
                          instructions="123-456-7890 x 123"
                        />
                      </Grid.Span>
                    </Grid>
                    <Checkbox.Field
                      className={styles.PACreateFormCheckboxNoLabel}
                      name="is_urgent"
                      label="Urgent - The patient is facing loss of life or life-altering loss of ability within 30 days"
                      testId="test__is_urgent_checkbox"
                    />
                  </>
                )}
              </Stack>

              {caseType === "new" && (
                <>
                  <Stack>
                    <Heading>Patient</Heading>
                    {patientError && <Message appearance={Messages.Error} visible>{patientError}</Message>}
                    <Grid>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field testId="test__member_id" label="Member ID" name="external_member_id" required />
                      </Grid.Span>
                      <Grid.Break />
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field testId="test__first_name" label="First Name" name="member_first_name" required />
                      </Grid.Span>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field testId="test__last_name" label="Last Name" name="member_last_name" required />
                      </Grid.Span>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Datebox.Field zone="local" serializer={DateSerializer.Literal} testId="test__date_of_birth" label="Date of Birth" name="member_date_of_birth" instructions="mm/dd/yyyy" required />
                      </Grid.Span>
                    </Grid>
                  </Stack>

                  <Stack>
                    <Heading>Drug</Heading>
                    {drugError && <Message appearance={Messages.Error} visible>{drugError}</Message>}
                    <Grid>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <div data-testid="test__ndc">
                          <Stack spacing="mini">
                            <NDCDropdown required demoSearchValue={demoDrugSearchValue} />
                            <Body size="mini" style={{ color: "#3E5D6F" }}>
                              Search by drug name, NDC/GPI, or exact match for HCPCS code
                            </Body>
                          </Stack>
                        </div>
                      </Grid.Span>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field testId="test__quantity" label="Quantity (Units)" name="quantity_dispensed" instructions="Enter the quantity or units to be dispensed" validator={numberValidator} required />
                      </Grid.Span>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Textbox.Field testId="test__days_supply" label="Days Supply" name="days_supply" validator={numberValidator} required />
                      </Grid.Span>
                      <Grid.Span columns={12}>
                        <TextArea.Field testId="test__directions_for_use" label="Directions For Use" name="directions_for_use" required />
                      </Grid.Span>
                      <Grid.Span columns={three_six_twelve_columns}>
                        <Dropdown.Field testId="test__daw" name="daw_code" label="DAW Code" required>
                          {Object.keys(DAW_CODES).map((key) => (
                            <Dropdown.Option key={key} value={key}>{DAW_CODES[key]} </Dropdown.Option>
                          ))}
                        </Dropdown.Field>
                      </Grid.Span>
                    </Grid>
                  </Stack>

                  <Stack>
                    <Heading>Diagnosis Codes</Heading>
                    <DiagnosisCodeMultiSelect name="icd_10" required demoSearchValue={demoDiagnosisSearchValue} />
                  </Stack>
                </>
              )}


              <StackOrShelf spacing="small">
                {["development", "staging"].includes(currentEnvironment) && caseType === "new" && (
                  <Button
                    appearance={getThemeButton("secondary", primary_color)}
                    disabled={isSubmitting}
                    onClick={form.handleSubmit((values) => {
                      nextStepRef.current = FormStates.SendDraft;
                      return formSubmitted(values);
                    })}
                    testId="test__create_draft_button"
                  >
                    {isSubmitting && nextStepRef.current == FormStates.SendDraft ? <Shelf alignment="center" spacing="small"><Spinner /> Submitting</Shelf> : "Continue as Draft"}
                  </Button>
                )}

                {nextStepRef.current != FormStates.SendDraft && (
                  <SubmitButton
                    appearance={getThemeButton("primary", primary_color)}
                    testId="test__create_button"
                  >
                    Continue
                  </SubmitButton>
                )}

                {["development", "staging"].includes(currentEnvironment) && caseType === "new" && (
                  <Button
                    appearance={getThemeButton("secondary", primary_color)}
                    onClick={fillDemoDataClick}
                  >
                    Fill Demo Data
                  </Button>
                )}
                <Shelf.Spacer />
              </StackOrShelf>
            </Stack>
          </Form>}
      {!editView
        && <Stack spacing="large">
          <Stack>
            <StackOrShelf>
              <Subtitle>Case</Subtitle>
            </StackOrShelf>

            <DetailsList data={getDetails()} />

            {!!previousCaseId && (
              <StackOrShelf>
                <Body>Previous Case ID</Body>
                <Shelf.Spacer />
                <a
                  data-testid="test__previous_case_id_link"
                  href={`${getThemeURL("PA_CASE")}?case_id=${previousCaseId}&prescriber_npi=${form.getValues("npi")}&external_member_id=${form.getValues("external_member_id")}&member_date_of_birth=${encodeURIComponent(form.getValues("member_date_of_birth") ?? "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  {previousCaseId} ↗
                </a>
              </StackOrShelf>
            )}
          </Stack>

          <div className={`${styles.PACreateFormTestClaimResult} ${validateResult.pa_needed ? "" : styles.NoReview}`} style={{ "--primary-color": primary_color } as React.CSSProperties}>
            <Heading className={`${styles.PACreateFormTestClaimResultHeading} ${validateResult.pa_needed ? "" : styles.NoReview}`} style={{ "--primary-color": primary_color } as React.CSSProperties}>
              Test Claim Result
            </Heading>
            <Body className={styles.PACreateFormTestClaimResultBody} style={{ "--primary-color": primary_color } as React.CSSProperties}>
              {validateResult.result
                && <><Semibold>{validateResult.result}</Semibold><br /></>
              }
              {validateResult.reject_codes && validateResult.pa_needed
                && <span>Reject code(s): {validateResult.reject_codes.join(", ")}</span>
              }
              {!validateResult.pa_needed
                && <span>Test claim accepted</span>
              }
            </Body>
          </div>
          {!validateResult.pa_needed
            && <StackOrShelf>
              <Button appearance="secondary" onClick={goToPAHome}>Cancel</Button>
              <Shelf.Spacer />
            </StackOrShelf>
          }
          {validateResult.pa_needed && validateResult.show_dispensing_site && !hasDispensingSite
            && <Form form={form} onSubmit={dispensingSiteFormSubmitted}>
              <Stack>
                <Grid>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Dropdown.Field testId="test__dispensing_site" label="Dispensing Site" name="dispensing_site" required>
                      {Object.keys(DispensingSiteDisplay).map((key) => (
                        <Dropdown.Option key={key} value={key}>{DispensingSiteDisplay[key]}</Dropdown.Option>
                      ))}
                    </Dropdown.Field>
                  </Grid.Span>
                  {dispensingSite === "prescriber_administered"
                    && <Grid.Span columns={three_six_twelve_columns}>
                      <Textbox.Field
                        testId="test__facility_npi"
                        label="Dispensing Provider NPI"
                        name="facility_npi"
                        validator={npiValidator}
                        required
                      />
                    </Grid.Span>
                  }
                </Grid>
                <StackOrShelf spacing="small">
                  <SubmitButton appearance={getThemeButton("primary", primary_color)} testId="test__continue_button">Continue</SubmitButton>
                  <Shelf.Spacer />
                </StackOrShelf>
              </Stack>
            </Form>
          }
        </Stack>}
    </Stack>
  );
};

export default CaseTestClaimForm;
